import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {useQuery} from "@tanstack/react-query";
import homeService from "../../../services/web/home-service.js";
import "./css/home.css"
import {FaChartBar, FaDownload} from "react-icons/fa";
import {
    FaArrowRightArrowLeft,
    FaRotateLeft,
    FaSwatchbook,
    FaTriangleExclamation,
    FaXmark
} from "react-icons/fa6";
import Button from "../../../components/button/button.jsx";
import {getAssetPath} from "../../../utils/get-asset-path.js";
import InputForm from "../../../components/input/input-form.jsx";
import {v4} from "uuid";
import projectUtils from "../../../utils/project-utils.js";
import {toast} from "react-toastify";
import SelectComponent from "../../../components/select/select-component.jsx";
import Alert from "../../../components/alert/alert.jsx";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {resolveTo} from "../../app-shell/utils/menu-utils.js";
import useSession from "../../../hooks/use-session.js";
import sentryMenu from "../../app-shell/resources/sentry-menu.js";
import {buildMenu, sessionToContext, withVmsCards} from "../../app-shell/utils/menu-filter.js";
import {homeMenuItems, resolveSavedCards} from "./utils/home-menu.js";

const Home = () => {
    const navigate = navigateHook();

    const [cards, setCards] = useState([]);
    const [addCards, setAddCards] = useState([]);
    const [removedDefaultCards, setRemovedDefaultCards] = useState([])
    const [edit, setEdit] = useState(false);

    const [menu, setMenu] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const blurred = useRef(false);
    const focus = useRef(false);

    const {data: userHome, refetch, isSuccess} = useQuery({
        queryFn: homeService.userHome,
        queryKey: ['userHome'],
    });

    const {data: status} = useQuery({
        queryFn: () => homeService.status(),
        queryKey: ['status'],
    });

    const {data: session} = useSession();

    const menuContext = useMemo(() => sessionToContext(session), [session]);

    const defaultCards = useMemo(() => {
        const staticCards = withVmsCards(sentryMenu.staticMenu, session?.vmsCards);

        return buildMenu(staticCards, menuContext, "sentry");
    }, [menuContext, session?.vmsCards]);

    const menuItems = useMemo(() => homeMenuItems(menuContext), [menuContext]);

    const prepareCards = useCallback(() => {
        const {homeDefinitions, removedIds} = userHome

        return [...defaultCards, ...resolveSavedCards(homeDefinitions, menuContext)]
            .filter(c => !removedIds?.includes(c.id))
            .map(c => ({ ...c, tempId: v4() }));
    }, [defaultCards, menuContext, userHome])

    useEffect(() => {
        if (!isSuccess) return;
        setCards(prepareCards());
    }, [isSuccess, userHome, defaultCards]);

    useEffect(() => {
        setMenu(
            menuItems
                .filter((item) => !defaultCards.some(card => card.url === item.url) && !cards.some(card => card.url === item.url))
                .map(item => ({...item, label: item.path, value: item.id}))
        )
    }, [menuItems, cards, defaultCards]);

    const faIcon = {
        download: <FaDownload className={"home-icon"} color={"#031b34"}/>,
        "home-material-movements": <FaArrowRightArrowLeft className={"home-icon"} color={"#031b34"}/>,
    }

    const changeToEdit = () => {
        const cardsEditable = cards.filter(card => card.editable);
        const newCards = [...defaultCards, ...cardsEditable].map(card => {
            const next = {...card};
            if (!next.editable) next.removed = !cards.find(c => c.id === next.id);
            next.tempId = v4();
            return next;
        });

        setCards(newCards);
        setEdit(true);
    }

    const undoCard = (cardId) => {
        setCards(prev => prev.map(c => ({...c, removed: c.id === cardId ? false : c.removed})))
        setRemovedDefaultCards(prev => prev.filter(c => c.id !== cardId));
    }

    const removeCard = (card) => {
        if (!card.editable) {
            setRemovedDefaultCards(prev => ([...prev, card]));
            setCards(prev => prev.map(c => ({...c, removed: c.id === card.id ? true : c.removed ?? false})))
            return
        }

        setCards(prev => prev.filter(c => c.tempId !== card.tempId));
    }

    const cancelEdit = () => {
        setCards(prepareCards());
        setAddCards([]);
        setEdit(false);
    }

    const resetToDefault = () => {
        projectUtils.swalMessage(
            'warning',
            'Atenção',
            'Deseja remover as alterações?',
            'Sim',
            true,
            'Não',
            ({isConfirmed}) => {
                if (!isConfirmed) return;
                submit([], []);
            }
        );
    }

    const submit = (defaultCard, defaultRemoved) => {
        const card = cards.filter(card => card.editable).map(({ url, ...card }) => card);
        setIsLoading(true);

        const formData = new FormData();
        formData.append("cards", JSON.stringify(defaultCard ?? card));
        formData.append("removedDefaultCards", JSON.stringify(defaultRemoved ?? removedDefaultCards));

        homeService.update(formData)
            .then(() => {
                toast.success("Salvo com sucesso");
                setAddCards([]);
                refetch();

                setEdit(false);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const addItem = () => {
        setAddCards(prev => ([...prev, {id: v4(), nameCustom: ""}]))
    }

    const addCard = (card, selected) => {
        if (!selected) return;

        setAddCards(prev => prev.filter(p => p.id !== card.id));

        const newCard = {...selected};

        newCard.name = card.nameCustom || newCard.homeName || newCard.name;
        newCard.editable = true;
        newCard.tempId = v4();

        delete newCard.value;
        delete newCard.label;

        setCards(prev => ([...prev, newCard]));
    }

    const openView = (card) => {
        if (edit) return

        if (card.id?.startsWith("card-vms-")) {
            const vmsTab = window.open("", "_blank");
            if (!vmsTab) {
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS. Verifique se os pop-ups estão habilitados.", "Ok");
                return;
            }

            vmsTab.opener = null;
            setIsLoading(true);
            homeService.accessVms(card.url)
                .then((url) => {
                    vmsTab.location.href = url;
                })
                .catch(() => {
                    vmsTab.close();
                    projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
                })
                .finally(() => setIsLoading(false));

            return;
        }

        if (card.app) {
            window.onblur = function () {
                blurred.current = true;
            }

            homeService.loginHash()
                .then((loginHash) => {
                    window.location.href = `installer://sentry?#module==${card.appName}` +
                        `#autoLoginToken==${loginHash}` +
                        `#serverProtocol==${window.location.protocol.replace(":", "")}` +
                        `#serverIp==${window.location.host}`;


                    let isHttps = window.location.protocol.includes("https");
                    setTimeout(function () {

                        if (blurred.current) {
                            window.onfocus = function () {
                                focus.current = true;
                            };

                            const interval = setInterval(() => {
                                if (focus.current === true) {
                                    clearInterval(interval);
                                    startLongPolling(loginHash, card.appName);
                                }
                            }, 500);

                        } else {
                            startLongPolling(loginHash, card.appName);
                        }
                    }, isHttps ? 500 : 1000);
                })
                .catch(() => {
                    projectUtils.swalErrorMessage("Erro ao validar o usuário ativo", "Por favor, faça login novamente para continuar.", "Ok")
                })

            return;
        }

        const to = resolveTo(card.url);
        if (!to) return;

        navigate(to, true);
    }

    async function startLongPolling(uuid, cardTitle) {
        const title = cardTitle === "sentryview" ? "Sentry View" : cardTitle;
        console.log(title)

        setIsLoading(true);
        homeService.poll(uuid).then((response) => {

                if (response.status === 200) {
                    toast.success(`O ${title} foi aberto com sucesso!`)
                }
                setIsLoading(false);
            }
        ).catch(error => {
            if(error?.response?.status != null && error?.response?.status === 408){
                setIsLoading(false);
                projectUtils.swalErrorMessage(`Não foi possível iniciar o ${title}`, `Por favor, verifique se a instalação está correta ou atualize para a versão mais recente.`, "Ok")
            }else{
                setIsLoading(false);
            }
        })
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Home</Template.Title>

                {
                    !edit &&
                    <Button target={"default"} onClick={changeToEdit}>
                        Editar
                    </Button>
                }

                {
                    edit &&
                    <div className={"flex gap-2"}>
                        <Button target={"secondary"} onClick={cancelEdit}>
                            Cancelar
                        </Button>
                        <Button target={"danger"} onClick={resetToDefault}>
                            Remover alterações
                        </Button>
                        <Button onClick={() => submit()} actionFeedbackType='save'>

                            Salvar edição
                        </Button>
                    </div>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                {
                    status?.daysToExpireLicense && !!status?.daysToExpireLicense &&
                    <Alert className={"flex gap-5 items-center !px-4"} target={"danger"}>
                        <FaTriangleExclamation size={48}/>
                        <h3>Atenção. Sua licença expira em ${status?.daysToExpireLicense} dias</h3>
                    </Alert>
                }
                {
                    status?.checkServerStatus &&
                    <Alert className={"flex gap-5 items-center !px-4"} target={"danger"}>
                        <FaTriangleExclamation size={48}/>
                        <h3>Servidor com problemas! Entre em contato com o suporte técnico</h3>
                    </Alert>
                }
                {
                    status?.checkTimezone &&
                    <Alert className={"flex gap-5 items-center !px-4"} target={"danger"}>
                        <FaTriangleExclamation size={48}/>
                        <h3>Timezone do servidor incorreta</h3>
                    </Alert>
                }

                <div className={"home grid grid-cols-12 gap-3"}>
                    {
                        cards?.map((card, i) => (
                            <section key={card.id + "-" + i} className={"col-span-12 lg:col-span-4 xl:col-span-3" + (card.removed ? " opacity-50" : "")}>
                                <a target="_blank" className={"!text-[#2a3947] font-bold cursor-pointer"} onClick={() => openView(card)}>
                                    <div className={"h-[180px] shadow border-l-4 border-l-[var(--blue-darker)] bg-white rounded-sm relative"}>
                                        {
                                            edit && card.removed &&
                                            <span className={"absolute text-[15px] right-[10px] top-[10px]"} onClick={() => undoCard(card.id)} title="Voltar">
                                                <FaRotateLeft />
                                            </span>
                                        }

                                        {
                                            edit && !card.removed &&
                                            <span className={"absolute text-[15px] right-[10px] top-[10px]"} onClick={() => removeCard(card)} title="Excluir">
                                                <FaXmark color={"#ed5565"}/>
                                            </span>
                                        }

                                        <h2 className={"p-5"}>
                                            {card.name}
                                        </h2>

                                        <div className={"h-[90px]"}>
                                            {
                                                card.image &&
                                                <div className={["home-icon", card.icon].join(" ")} />
                                            }

                                            {
                                                !card.image && card.icon &&
                                                faIcon[card.id]
                                            }

                                            {
                                                !card.image && !card.icon &&
                                                <div className={"home-icon sw-link"} />
                                            }
                                        </div>
                                    </div>
                                </a>
                            </section>
                        ))
                    }

                    {
                        addCards?.map(card => (
                            <section key={card.id} className={"md:col-span-12 lg:col-span-4 xl:col-span-3"}>
                                <div
                                    className={"h-[180px] shadow border-l-4 border-l-[var(--blue-bright)] bg-white rounded-sm relative !text-[#2a3947] cursor-pointer flex flex-col py-5 px-4 space-y-5"}>
                                    <InputForm
                                        id={"name"}
                                        value={card.nameCustom}
                                        label={"Nome personalizado"}
                                        onChange={({target: {value}}) => setAddCards(prev => prev.map(item => item.id === card.id ? { ...item, nameCustom: value } : item) )}
                                    />

                                    <SelectComponent
                                        name="status"
                                        value={""}
                                        options={menu}
                                        label={"Selecione um item"}
                                        hideOptionPath
                                        onChange={(selected) => addCard(card, selected)}
                                    />
                                </div>
                            </section>
                        ))
                    }

                    {
                        edit &&
                        <section className={"md:col-span-12 lg:col-span-4 xl:col-span-3"}>
                            <div onClick={addItem}
                                className={"h-[180px] shadow border-l-4 border-l-[var(--blue-bright)] bg-white rounded-sm relative !text-[#2a3947] font-bold cursor-pointer flex flex-col items-center justify-center"}>
                                <img className={"w-[50px] mb-[1.2em]"}
                                     src={getAssetPath("img/dashboard-svg/sw.add.card.svg")} alt=""/>
                                
                                <p className="font-bold">Clique para <b>adicionar</b> um <b>novo card</b></p>
                            </div>
                        </section>
                    }
                </div>
            </Template.Body>
        </Template>
    );
};

export default Home;